/**
 * Route guards.
 *
 * Single source of truth for app-level access decisions, per
 * `FRONTEND_AUTH_PERMISSIONS.md`. These guards are UX gates only — the
 * backend remains the authority. They sit downstream of
 * `SessionBootstrap`, which means by the time any guard runs, the auth
 * store has either confirmed access (`accessToken + user`) or confirmed
 * guest (`accessToken === null`).
 *
 *   GuestRoute      — login/register pages. Authenticated users go home.
 *   ProtectedRoute  — requires an authenticated session. Otherwise login.
 *   BusinessGuard   — requires a registered business. Otherwise the
 *                     onboarding redirect.
 *
 * "Authenticated" here means `accessToken` is populated AND `user` is
 * loaded. That distinction matters: a refresh during the app session
 * temporarily replaces just the access token, but a missing user means
 * something went wrong (e.g. partial bootstrap) and we should treat the
 * session as not-yet-ready.
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/state/stores/auth.store';
import {
  ROUTE_PATHS,
  buildRouteWithReturnTo,
  buildLoginRecoveryPath,
  protectedRouteRecoveryReason,
  safeAuthReturnTarget,
} from '@/navigation';
import { hasBusinessContext } from '@/core/auth/has-business-context';
import { SMS_AUTH_ENABLED } from '@/core/release-capabilities';

export function GuestRoute({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  if (accessToken && user) {
    return <Navigate to={ROUTE_PATHS.HOME} replace />;
  }
  return <>{children}</>;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!accessToken || !user) {
    return (
      <Navigate
        to={buildLoginRecoveryPath({
          returnTo: `${location.pathname}${location.search}${location.hash}`,
          reason: protectedRouteRecoveryReason(location.pathname),
        })}
        replace
      />
    );
  }
  return <>{children}</>;
}

export function BusinessGuard({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  // Phase 4.4c-3 — tenant-ready means BOTH `hasRegisteredBusiness`
  // AND a real `businessId`. A stale shape like
  // `{ hasRegisteredBusiness: true, businessId: null }` would
  // otherwise slip into the tenant shell and 403 on every API call
  // (the apiClient interceptor derives `x-business-id` from
  // `user.businessId`). The shared predicate is the contract.
  if (user && !hasBusinessContext(user)) {
    return <Navigate to={ROUTE_PATHS.ONBOARDING} replace />;
  }
  return <>{children}</>;
}

/**
 * Phase 4.4c-2 — `/onboarding` must NOT be reachable by users who
 * already have a business. Belt-and-braces against a stale tab or a
 * deep-link from a prior session.
 *
 * Phase 4.4c-3 — uses `hasBusinessContext` so a user with a stale
 * `{ hasRegisteredBusiness: true, businessId: null }` shape can still
 * REACH the onboarding wizard to recover. Without the predicate we'd
 * bounce them home, where BusinessGuard would bounce them right back
 * — a stuck-in-onboarding loop the predicate prevents.
 */
export function OnboardingGuard({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (hasBusinessContext(user)) {
    const returnTo = safeAuthReturnTarget(new URLSearchParams(location.search).get('returnTo'));
    return (
      <Navigate
        to={returnTo && returnTo !== ROUTE_PATHS.ONBOARDING ? returnTo : ROUTE_PATHS.HOME}
        replace
      />
    );
  }
  if (user && SMS_AUTH_ENABLED && !user.phoneVerifiedAt) {
    return (
      <Navigate
        to={buildRouteWithReturnTo(
          ROUTE_PATHS.VERIFY_PHONE,
          new URLSearchParams(location.search).get('returnTo'),
        )}
        replace
      />
    );
  }
  return <>{children}</>;
}
